import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRightToInformationWizardContext } from '../../../hooks/useCaseRequestWizard';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import { isTodayOrFutureIsoDate } from '../../../utils/caseFieldValidation';
import { validationDateFutureOnly } from '../../../utils/validationI18n';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { SectionCard } from '../WizardComponents';
import { RightToInformationWizardActions } from './RightToInformationWizardActions';
import {
  rightToInformationWizardPath,
  RightToInformationWizardShell,
  FieldError,
  inpClass } from './RightToInformationWizardShell';
import { useRightToInformationLookups } from './useRightToInformationLookups';
import { useWizardFieldLabels } from '../useWizardFieldLabels';
import {
  isHighUrgencyLevel,
  minDeadlineDateForRightToInformation,
  validateRightToInformationStep } from './rightToInformationStepValidation';

const MAX_TEXT = 2000;

export default function RightToInformationStep2TimelinePage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useRightToInformationWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { urgencyLevels } = useRightToInformationLookups('step2');
  const minDeadlineDate = useMemo(() => minDeadlineDateForRightToInformation(), []);
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateRightToInformationStep(2, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <RightToInformationWizardShell currentStep={2} draft={draft}>
      <SectionCard title={f.sectionTimeline}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.urgencyLevel} <span className="req">*</span>
            </label>
            <WizardSelect
              id="rti-urgency"
              hasError={Boolean(errors.urgencyLevelId)}
              value={draft.urgencyLevelId}
              placeholder={f.selectUrgencyLevel}
              options={urgencyLevels.map((u) => ({ id: u.id, label: u.name }))}
              onChange={(id) => {
                const name = urgencyLevels.find((u) => u.id === id)?.name ?? '';
                setDraft((d) => ({
                  ...d,
                  urgencyLevelId: id,
                  urgencyLevelName: name }));
                if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                clearErr('urgencyLevelId');
              }}
            />
            <FieldError message={errors.urgencyLevelId} />
          </div>
          <div className="ccr-fg">
            <label>{f.deadlineIfAny}</label>
            <FormattedDateInput
              id="rti-deadline"
              className={inpClass(Boolean(errors.deadline))}
              value={draft.deadline}
              minDate={minDeadlineDate}
              enableYmdViews
              onChange={(next) => {
                setDraft((d) => ({ ...d, deadline: next }));
                clearErr('deadline');
                if (next && !isTodayOrFutureIsoDate(next)) {
                  setErrors((e) => ({ ...e, deadline: validationDateFutureOnly() }));
                }
              }}
            />
            <FieldError message={errors.deadline} />
          </div>
        </div>
        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>
            {f.urgencyJustification} {urgencyJustificationRequired ? <span className="req">*</span> : null}
          </label>
          <textarea
            id="rti-urgencyJustification"
            className={inpClass(Boolean(errors.urgencyJustification))}
            rows={3}
            value={draft.urgencyJustification}
            onChange={(e) => {
              setDraft((d) => ({ ...d, urgencyJustification: maxLen('urgencyJustification', e.target.value, MAX_TEXT) }));
            }}
            placeholder={f.urgencyJustificationReason}
          />
          <FieldError message={errors.urgencyJustification} />
        </div>
      </SectionCard>

      <RightToInformationWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={2}
        onBack={() => nav(rightToInformationWizardPath(1, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(rightToInformationWizardPath(3, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </RightToInformationWizardShell>
  );
}
